import { useEffect, useState } from "react";
import { getUserLoadRequests } from "apis/myRequestsService";
import { MyRequestParams, MyRequestResponse } from "./types";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";

const DEFAULT_PAGE_SIZE = 10;

// Owns the "Your Requests" data: fetching, pagination, sort, and status filter.
// Keeps the page component focused on presentation.
export function useLoadRequests() {
  const { showLoader, hideLoader } = useGlobalLoader();

  const [flightsDetails, setFlightsDetails] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("departure");
  const [sortOrder, setSortOrder] = useState("asc");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [clearSelectAll, setClearSelectAll] = useState(false);

  // silent=true skips the global loader — used for the background refresh after
  // an optimistic delete so the page refills without a loader flash.
  const fetchLoadRequests = async (silent = false) => {
    if (!silent) {
      showLoader();
      setLoading(true);
    }
    setError(null);

    try {
      const params: MyRequestParams = {
        page: currentPage,
        size: pageSize,
        sortBy,
        sortOrder,
        status,
      };
      const data = await getUserLoadRequests(params);
      // If the current page is now empty (e.g. the last item was deleted), step
      // back a page; the effect refetch will load it.
      if (data.items.length === 0 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
        return;
      }
      setFlightsDetails(data);
    } catch (err) {
      console.error("Error fetching load requests:", err);
      setError("Failed to fetch load requests");
    } finally {
      if (!silent) {
        setLoading(false);
        setTimeout(() => hideLoader(), 500);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchLoadRequests();
  }, [status, currentPage, sortBy, sortOrder, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightsDetails.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
    // Reset the "Select All" checkbox briefly so the child clears its selection.
    setClearSelectAll(true);
    setTimeout(() => setClearSelectAll(false), 0);
  };

  // Change rows-per-page and jump back to the first page so the new page size
  // is applied from the top of the list.
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return {
    flightsDetails,
    loading,
    error,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    status,
    setStatus,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    clearSelectAll,
    fetchLoadRequests,
    handlePageChange,
    handlePageSizeChange,
  };
}
